import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Button, Divider, Typography, useTheme } from '@mui/material'
import type {
  ClaudeSession,
  Command,
  ConversationTurnEntry,
} from '@/features/events/conversation/state/types'
import {
  ErrorRecoveryType,
  isInteractable,
  isUserOptionSelection,
} from '@/features/events/conversation/state/types'
import { claudeApiErrorFrom, isClaudeApiError } from '@/features/events/conversation/models'
import { ERRORED_TOOL_NAME } from '@/infra/sse/conversation/decryption/resolveNotificationContext'
import {
  selectToolPermissionContextRenderer,
  selectPlanApprovalContextRenderer,
} from '@/features/events/conversation/utils/contextRendererSelectors'
import { isExitPlanModeToolInput } from '@/features/events/shared/types/tool-inputs'
import { isUserOptionsActive } from '@/features/events/shared/types'
import type { UserOption } from '@/features/events/shared/types/UserOption'
import { UserOptionsRenderer } from '../dialogs/UserOptionsRenderer'
import { NotificationBubble } from './NotificationBubble'

interface ErroredEventBubbleProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly onOptionSelect?:
    | ((optionIndex: number, event: ConversationTurnEntry) => void)
    | undefined
  readonly onEventDetailsClick?: ((event: ConversationTurnEntry) => void) | undefined
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
}

type ErrorType = 'api_error' | 'system_error'

interface ErroredEventData {
  readonly errorType: ErrorType
  readonly errorMessage: string
  readonly statusCode: number | null
}

interface RecoverySectionProps {
  readonly onRecover: () => void
  readonly label: string
}

export const ErroredEventBubble: FC<ErroredEventBubbleProps> = ({
  event,
  session,
  latestEvent,
  allUserCommands,
  onOptionSelect,
  onEventDetailsClick,
  submitErrorRecovery,
}) => {
  const theme = useTheme()
  const { errorType, errorMessage, statusCode } = useErroredEventData(event)

  const hasToolContext =
    event.notificationContext?.toolName !== undefined &&
    event.notificationContext.toolName !== ERRORED_TOOL_NAME

  const handleSystemErrorRecovery = useCallback(() => {
    void submitErrorRecovery(event, ErrorRecoveryType.NOTIFICATION_CONTEXT_RECOVERY)
  }, [submitErrorRecovery, event])

  const handleApiErrorRecovery = useCallback(() => {
    void submitErrorRecovery(event, ErrorRecoveryType.API_ERROR_RECOVERY)
  }, [submitErrorRecovery, event])

  if (errorType === 'api_error') {
    const statusDisplay = statusCode ? ` (${statusCode})` : ''
    return (
      <NotificationBubble
        event={event}
        session={session}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        onOptionSelect={onOptionSelect}
        onEventDetailsClick={onEventDetailsClick}
        customBorderColor={theme.palette.error.light}
        customSelectionColor={theme.palette.error.light}
      >
        <Typography sx={errorHighlightSx(theme)}>API Error</Typography>

        <Typography sx={bodySx}>
          {errorMessage}
          {statusDisplay}
        </Typography>

        <RecoverySection onRecover={handleApiErrorRecovery} label="Retry" />
      </NotificationBubble>
    )
  }

  if (hasToolContext) {
    const { toolName: ctxToolName, toolInput: ctxToolInput } = event.notificationContext
    const contextRenderer = isExitPlanModeToolInput(ctxToolInput)
      ? selectPlanApprovalContextRenderer(ctxToolInput)
      : selectToolPermissionContextRenderer(ctxToolName, ctxToolInput)

    const isPlanApproval = isExitPlanModeToolInput(ctxToolInput)
    const syntheticOptions: UserOption[] = isPlanApproval
      ? [
          { index: 1, option: 'Approve' },
          { index: 2, option: 'Reject' },
          { index: 3, option: 'Something else' },
        ]
      : [
          { index: 1, option: 'Allow' },
          { index: 2, option: 'Always allow' },
          { index: 3, option: 'Something else' },
        ]

    const isSyntheticActive =
      isInteractable(session) && isUserOptionsActive(event, latestEvent, allUserCommands)

    const selectedCommand = allUserCommands.find(
      (cmd) => isUserOptionSelection(cmd) && cmd.refEventId === event.eventId,
    )
    const selectedOptionIndex = selectedCommand?.userOption?.index
    const customPrompt = selectedCommand?.userPrompt ?? null

    return (
      <NotificationBubble
        event={event}
        session={session}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        onOptionSelect={onOptionSelect}
        onEventDetailsClick={onEventDetailsClick}
        customBorderColor={theme.palette.error.light}
        customSelectionColor={theme.palette.error.light}
        showUserOptions={false}
      >
        {contextRenderer}

        <Divider sx={dividerSx} />

        <Typography sx={errorHighlightSx(theme)}>
          We failed to deliver user options. Review the request and select a response.
        </Typography>

        {onOptionSelect && (
          <UserOptionsRenderer
            options={syntheticOptions}
            isActive={isSyntheticActive}
            onOptionSelect={(index) => onOptionSelect(index, event)}
            showTitle={true}
            selectionColor={theme.palette.error.light}
            {...(selectedOptionIndex !== undefined && { selectedOptionIndex })}
            {...(customPrompt && { customPrompt })}
          />
        )}

        <RecoverySection onRecover={handleSystemErrorRecovery} label="Provide options as text" />
      </NotificationBubble>
    )
  }

  return (
    <NotificationBubble
      event={event}
      session={session}
      latestEvent={latestEvent}
      allUserCommands={allUserCommands}
      onOptionSelect={onOptionSelect}
      onEventDetailsClick={onEventDetailsClick}
      customBorderColor={theme.palette.error.light}
      customSelectionColor={theme.palette.error.light}
      showUserOptions={false}
    >
      <Typography sx={errorHighlightSx(theme)}>{errorMessage}</Typography>

      <Typography sx={bodySx}>We failed to deliver this request due to internal error</Typography>

      <RecoverySection onRecover={handleSystemErrorRecovery} label="Provide options as text" />
    </NotificationBubble>
  )
}

function useErroredEventData(event: ConversationTurnEntry): ErroredEventData {
  const isApiError = isClaudeApiError(event.agentResponse)
  const apiError =
    isApiError && event.agentResponse ? claudeApiErrorFrom(event.agentResponse) : null

  const errorType: ErrorType = isApiError ? 'api_error' : 'system_error'

  const errorMessage = useMemo(() => {
    if (isApiError && apiError) {
      return apiError.message
    }
    const toolInput = event.notificationContext?.toolInput as { error?: string } | undefined
    return toolInput?.error || 'We could not retrieve the request details'
  }, [isApiError, apiError, event.notificationContext?.toolInput])

  const statusCode = apiError?.statusCode ?? null

  return { errorType, errorMessage, statusCode }
}

const RecoverySection: FC<RecoverySectionProps> = ({ onRecover, label }) => (
  <Button variant="outlined" onClick={onRecover} sx={recoveryButtonSx}>
    {label}
  </Button>
)

const errorHighlightSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.error.light,
  mb: 2,
  fontWeight: 500,
  fontSize: 'inherit',
})

const dividerSx: SxProps<Theme> = {
  my: 2,
}

const bodySx: SxProps<Theme> = {
  mb: 2,
}

const recoveryButtonSx: SxProps<Theme> = {
  mt: 1,
}
